import axios from 'axios';
import { apiClient } from '@/modules/shared/services/apiClient';
import { ApiResponse } from '@/modules/shared/types';
import { ProgramUISettingsDto, CreateProgramUISettingsDto, PublicProgramUISettingsDto } from '../types/programUISettings';

/**
 * Roles allowed to call GET/PUT /programs/{id}/ui-settings.
 * Mirrors [Authorize(Roles = ...)] on ProgramUISettingsController — keep in sync.
 */
const UI_SETTINGS_ROLES = ['SuperAdmin', 'ProductAdmin', 'ProgramAdmin'];

/** True when the role may read authenticated program UI settings. */
export function canReadProgramUISettings(role?: string | null): boolean {
  return !!role && UI_SETTINGS_ROLES.includes(role);
}

export const programUISettingsService = {
  /**
   * Get UI settings for a program (authenticated).
   * A 403 means "UI settings unavailable for this role": resolves with
   * `data: null` (caller uses the default theme) instead of throwing.
   */
  async getUISettings(programId: string): Promise<ApiResponse<ProgramUISettingsDto | null>> {
    try {
      const response = await apiClient.get<ApiResponse<ProgramUISettingsDto | null>>(
        `/programs/${programId}/ui-settings`,
        { expectForbidden: true }
      );
      return response.data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 403) {
        return { success: true, message: 'UI settings unavailable for this role', data: null } as ApiResponse<null>;
      }
      throw err;
    }
  },

  /**
   * Create or update UI settings for a program
   */
  async saveUISettings(
    programId: string,
    dto: CreateProgramUISettingsDto
  ): Promise<ApiResponse<ProgramUISettingsDto>> {
    const response = await apiClient.put<ApiResponse<ProgramUISettingsDto>>(
      `/programs/${programId}/ui-settings`,
      dto
    );
    return response.data;
  },

  /**
   * Get public UI settings by program URL (unauthenticated - for login page)
   */
  async getPublicSettingsByUrl(programUrl: string): Promise<ApiResponse<PublicProgramUISettingsDto | null>> {
    const response = await apiClient.get<ApiResponse<PublicProgramUISettingsDto | null>>(
      `/programs/public/ui-settings/by-url`,
      { params: { programUrl } }
    );
    return response.data;
  },

  /**
   * Get public UI settings by program code (unauthenticated - for login page)
   */
  async getPublicSettingsByCode(programCode: string): Promise<ApiResponse<PublicProgramUISettingsDto | null>> {
    const response = await apiClient.get<ApiResponse<PublicProgramUISettingsDto | null>>(
      `/programs/public/ui-settings/by-code/${programCode}`
    );
    return response.data;
  },
};
